import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Boxes,
  SlidersHorizontal,
  Fingerprint,
  Target,
  Users2,
  Copy,
  Plus,
  Save,
} from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { EmptyState, Skeleton } from '@/components/States'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { useToast } from '@/providers/UiProviders'
import { ModListScreen } from './ModListScreen'
import { makeFetcher } from './modContentApi'
import {
  createMlModel,
  deleteMlModel,
  updateMlModel,
  createPhash,
  deletePhash,
  fetchMlModels,
  fetchModelQuality,
  fetchInterRater,
  ML_STR as S,
  type MlModelRow,
  type PhashRow,
  type SimilarCluster,
  type ModelQuality,
  type InterRaterData,
} from './modMlApi'

/**
 * A6 ML-og'ir moderatsiya ekranlari — HAQIQIY funksional (skelet EMAS):
 *   A6.23 model-registry / A6.25 threshold / A6.27 pHash → mod_ml_models /
 *   mod_hashes ustidan CRUD; A6.24 sifat / A6.38 inter-rater / A6.18 o'xshash
 *   holatlar → mod_decisions / mod_decision_errors ustidan REAL hisoblanadi.
 */

const addBtnCls =
  'inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover'

/* ── A6.23 — ML modellar reyestri (CRUD) ──────────────────────────── */

export function ModMlModelsScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const columns = useMemo<AdminColumn<MlModelRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'name', header: S.name, type: 'text' },
      { key: 'version', header: S.version, type: 'text' },
      { key: 'kind', header: S.kind, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'threshold', header: S.threshold, type: 'number' },
      { key: 'created_by', header: S.addedBy, type: 'text' },
      { key: 'updated_at', header: S.updatedAt, type: 'date' },
    ],
    [],
  )
  const fetcher = useMemo(
    () =>
      makeFetcher<MlModelRow>(
        '/admin/moderation/ml-models',
        'models',
        (r) => `${r.name} ${r.version} ${r.kind} ${r.status}`,
      ),
    [reloadKey],
  )
  const add = useCallback(async () => {
    const name = (window.prompt(S.namePrompt) ?? '').trim()
    if (!name) return
    const version = (window.prompt(S.versionPrompt, 'v1') ?? 'v1').trim() || 'v1'
    const kind = (window.prompt(S.kindPrompt, 'toxicity') ?? 'other').trim() || 'other'
    const status = (window.prompt(S.statusPrompt, 'draft') ?? 'draft').trim() || 'draft'
    const th = Number.parseFloat(window.prompt(S.thresholdPrompt, '0.5') ?? '0.5')
    try {
      await createMlModel({
        name,
        version,
        kind,
        status,
        threshold: Number.isFinite(th) ? th : 0.5,
      })
      toast(S.modelAdded, { tone: 'success' })
      setReloadKey((k) => k + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }, [toast])
  const remove = useCallback(
    async (row: MlModelRow) => {
      if (!window.confirm(S.confirmDelete)) return
      try {
        await deleteMlModel(row.id)
        toast(S.modelDeleted, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )
  const rowActions = useMemo<RowAction<MlModelRow>[]>(
    () => [{ id: 'del', label: S.delete, danger: true, onSelect: (r) => void remove(r) }],
    [remove],
  )
  return (
    <ModListScreen<MlModelRow>
      screenId="A6.23"
      title={S.models}
      description={S.modelsDesc}
      columns={columns}
      fetcher={fetcher}
      rowId={(r) => String(r.id)}
      icon={<Boxes size={28} strokeWidth={1.5} />}
      rowActions={rowActions}
      actions={
        <button onClick={() => void add()} className={addBtnCls}>
          <Plus size={14} />
          {S.add}
        </button>
      }
    />
  )
}

/* ── A6.25 — Threshold sozlash (mod_ml_models ustida config) ───────── */

const STATUSES = ['draft', 'staging', 'active', 'disabled'] as const

export function ThresholdTuningScreen() {
  const { toast } = useToast()
  const [models, setModels] = useState<MlModelRow[] | null>(null)

  const load = useCallback(() => {
    const ac = new AbortController()
    fetchMlModels(ac.signal).then((rows) => !ac.signal.aborted && setModels(rows))
    return () => ac.abort()
  }, [])
  useEffect(() => load(), [load])

  return (
    <>
      <PageHeader title={S.thresholds} screenId="A6.25" description={S.thresholdsDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {!models ? (
          <div className="max-w-2xl space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : models.length === 0 ? (
          <EmptyState
            title={S.noModels}
            icon={<SlidersHorizontal size={28} strokeWidth={1.5} />}
          />
        ) : (
          <div className="max-w-3xl space-y-2">
            {models.map((m) => (
              <ThresholdRow
                key={m.id}
                model={m}
                onSaved={() => {
                  toast(S.saved, { tone: 'success' })
                  load()
                }}
                onError={(e) =>
                  toast(S.failed, {
                    tone: 'error',
                    ...(e instanceof Error && e.message ? { description: e.message } : {}),
                  })
                }
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function ThresholdRow({
  model,
  onSaved,
  onError,
}: {
  model: MlModelRow
  onSaved: () => void
  onError: (e: unknown) => void
}) {
  const [threshold, setThreshold] = useState(String(model.threshold))
  const [status, setStatus] = useState(model.status)
  const [busy, setBusy] = useState(false)
  const dirty = threshold !== String(model.threshold) || status !== model.status

  const save = useCallback(async () => {
    const th = Number.parseFloat(threshold)
    setBusy(true)
    try {
      await updateMlModel(model.id, {
        threshold: Number.isFinite(th) ? th : model.threshold,
        status,
      })
      onSaved()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }, [threshold, status, model.id, model.threshold, onSaved, onError])

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      <div className="min-w-40 flex-1">
        <div className="text-sm font-medium text-primary">
          {model.name} <span className="text-tertiary">· {model.version}</span>
        </div>
        <div className="text-xs text-tertiary">{model.kind}</div>
      </div>
      <label className="flex items-center gap-1.5 text-sm text-secondary">
        {S.threshold}
        <input
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          className="w-20 rounded-md border border-border-default bg-surface-3 px-2 py-1 text-sm text-primary"
        />
      </label>
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        className="rounded-md border border-border-default bg-surface-3 px-2 py-1 text-sm text-primary"
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <button
        onClick={() => void save()}
        disabled={busy || !dirty}
        className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-40"
      >
        <Save size={13} />
        {S.save}
      </button>
    </div>
  )
}

/* ── A6.27 — Perceptual hash reyestri (CRUD) ───────────────────────── */

export function PerceptualHashScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const columns = useMemo<AdminColumn<PhashRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'hash', header: S.hash, type: 'text' },
      { key: 'category', header: S.category, type: 'text' },
      { key: 'source', header: S.source, type: 'text' },
      { key: 'added_by', header: S.addedBy, type: 'text' },
      { key: 'created_at', header: S.created, type: 'date' },
    ],
    [],
  )
  const fetcher = useMemo(
    () =>
      makeFetcher<PhashRow>(
        '/admin/moderation/phash',
        'hashes',
        (r) => `${r.hash} ${r.category} ${r.source}`,
      ),
    [reloadKey],
  )
  const add = useCallback(async () => {
    const hash = (window.prompt(S.hashPrompt) ?? '').trim()
    if (!hash) return
    const category = (window.prompt(S.category, 'csam') ?? 'other').trim() || 'other'
    const source = (window.prompt(S.sourcePrompt, '') ?? '').trim()
    try {
      await createPhash({ hash, category, source })
      toast(S.phashAdded, { tone: 'success' })
      setReloadKey((k) => k + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }, [toast])
  const remove = useCallback(
    async (row: PhashRow) => {
      if (!window.confirm(S.confirmDelete)) return
      try {
        await deletePhash(row.id)
        toast(S.phashDeleted, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )
  const rowActions = useMemo<RowAction<PhashRow>[]>(
    () => [{ id: 'del', label: S.delete, danger: true, onSelect: (r) => void remove(r) }],
    [remove],
  )
  return (
    <ModListScreen<PhashRow>
      screenId="A6.27"
      title={S.phash}
      description={S.phashDesc}
      columns={columns}
      fetcher={fetcher}
      rowId={(r) => String(r.id)}
      icon={<Fingerprint size={28} strokeWidth={1.5} />}
      rowActions={rowActions}
      actions={
        <button onClick={() => void add()} className={addBtnCls}>
          <Plus size={14} />
          {S.add}
        </button>
      }
    />
  )
}

/* ── A6.24 — Model sifati (precision/recall, real confusion matrix) ── */

export function ModelQualityScreen() {
  const [data, setData] = useState<ModelQuality | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    fetchModelQuality(ac.signal)
      .then(setData)
      .finally(() => !ac.signal.aborted && setLoading(false))
    return () => ac.abort()
  }, [])

  const empty = !loading && (!data || data.total_decisions === 0)

  return (
    <>
      <PageHeader title={S.quality} screenId="A6.24" description={S.qualityDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-3xl space-y-3">
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-40" />
              ))}
            </div>
            <Skeleton className="h-40 w-full" />
          </div>
        ) : empty || !data ? (
          <EmptyState title={S.noData} icon={<Target size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="max-w-3xl space-y-3">
            <div className="flex flex-wrap gap-2">
              <Tile label={S.precision} value={pct(data.precision)} accent />
              <Tile label={S.recall} value={pct(data.recall)} accent />
              <Tile label={S.f1} value={pct(data.f1)} />
              <Tile label={S.accuracy} value={pct(data.accuracy)} />
            </div>

            <div className="rounded-lg border border-border-default bg-surface-2 p-3">
              <div className="mb-2 text-sm font-medium text-secondary">{S.confusion}</div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Cell label={S.tp} value={data.tp} tone="text-emerald-400" />
                <Cell label={S.fp} value={data.fp} tone="text-danger" />
                <Cell label={S.fn} value={data.fn} tone="text-warning" />
                <Cell label={S.tn} value={data.tn} tone="text-secondary" />
              </div>
              <div className="mt-2 flex gap-4 text-xs text-tertiary">
                <span>
                  {S.totalDecisions}: <span className="text-secondary">{data.total_decisions.toLocaleString()}</span>
                </span>
                <span>
                  {S.totalErrors}: <span className="text-secondary">{data.total_errors.toLocaleString()}</span>
                </span>
              </div>
            </div>

            {data.by_action.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-border-default bg-surface-2">
                <div className="px-3 pt-3 text-sm font-medium text-secondary">{S.byAction}</div>
                <table className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="border-b border-border-default text-left text-xs text-tertiary">
                      <th className="px-3 py-2 font-medium">{S.action}</th>
                      <th className="px-3 py-2 text-right font-medium">{S.total}</th>
                      <th className="px-3 py-2 text-right font-medium">{S.overturned}</th>
                      <th className="px-3 py-2 text-right font-medium">{S.precision}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {data.by_action.map((a) => (
                      <tr key={a.action} className="hover:bg-surface-3">
                        <td className="px-3 py-2 text-primary">{a.action}</td>
                        <td className="px-3 py-2 text-right font-mono tabular text-secondary">{a.total}</td>
                        <td className="px-3 py-2 text-right font-mono tabular text-danger">{a.overturned}</td>
                        <td className="px-3 py-2 text-right font-mono tabular text-primary">{pct(a.precision)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

/* ── A6.38 — Kelishuv darajasi (inter-rater agreement, real) ───────── */

export function InterRaterScreen() {
  const [data, setData] = useState<InterRaterData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    fetchInterRater(ac.signal)
      .then(setData)
      .finally(() => !ac.signal.aborted && setLoading(false))
    return () => ac.abort()
  }, [])

  const empty = !loading && (!data || data.multi_rated === 0)

  return (
    <>
      <PageHeader title={S.interRater} screenId="A6.38" description={S.interRaterDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-3xl space-y-3">
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-40" />
              ))}
            </div>
            <Skeleton className="h-40 w-full" />
          </div>
        ) : empty || !data ? (
          <EmptyState title={S.noMultiRater} icon={<Users2 size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="max-w-3xl space-y-3">
            <div className="flex flex-wrap gap-2">
              <Tile label={S.agreement} value={`${data.agreement_pct.toFixed(1)}%`} accent />
              <Tile label={S.multiRated} value={data.multi_rated.toLocaleString()} />
              <Tile label={S.agreed} value={data.agreed.toLocaleString()} />
              <Tile label={S.disputed} value={data.disputed.toLocaleString()} />
            </div>

            <div className="overflow-x-auto rounded-lg border border-border-default bg-surface-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-default text-left text-xs text-tertiary">
                    <th className="px-3 py-2 font-medium">{S.target}</th>
                    <th className="px-3 py-2 text-right font-medium">{S.raters}</th>
                    <th className="px-3 py-2 text-right font-medium">{S.decisions}</th>
                    <th className="px-3 py-2 font-medium">{S.actions}</th>
                    <th className="px-3 py-2 font-medium">{S.agree}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {data.rows.map((r) => (
                    <tr key={r.id} className="hover:bg-surface-3">
                      <td className="px-3 py-2 font-mono text-primary">
                        {r.target_type}:{r.target_id}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular text-secondary">{r.raters}</td>
                      <td className="px-3 py-2 text-right font-mono tabular text-secondary">{r.decisions}</td>
                      <td className="px-3 py-2 text-secondary">{r.actions}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
                            r.agree
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : 'bg-danger/15 text-danger'
                          }`}
                        >
                          {r.agree ? '✓' : '✗'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A6.18 — O'xshash holatlar (clustering-lite, real) ─────────────── */

export function SimilarCasesScreen() {
  const columns = useMemo<AdminColumn<SimilarCluster>[]>(
    () => [
      { key: 'reason', header: S.reason, type: 'longtext' },
      { key: 'target_type', header: S.target, type: 'text' },
      { key: 'action', header: S.action, type: 'text' },
      { key: 'cases', header: S.cases, type: 'number' },
      { key: 'targets', header: S.targets, type: 'number' },
      { key: 'first_at', header: S.firstAt, type: 'date' },
      { key: 'last_at', header: S.lastAt, type: 'date' },
    ],
    [],
  )
  return (
    <ModListScreen<SimilarCluster>
      screenId="A6.18"
      title={S.similar}
      description={S.similarDesc}
      columns={columns}
      fetcher={makeFetcher<SimilarCluster>(
        '/admin/moderation/similar',
        'clusters',
        (r) => `${r.reason} ${r.target_type} ${r.action}`,
      )}
      rowId={(r) => r.id}
      icon={<Copy size={28} strokeWidth={1.5} />}
    />
  )
}

/* ── kichik prezentatsion yordamchilar ─────────────────────────────── */

function Tile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="inline-flex min-w-36 flex-col gap-1 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      <div className="text-xs text-tertiary">{label}</div>
      <div className={`font-mono text-xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>{value}</div>
    </div>
  )
}

function Cell({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-md border border-border-subtle bg-surface-3 px-3 py-2">
      <div className={`font-mono text-lg tabular ${tone}`}>{value.toLocaleString()}</div>
      <div className="mt-0.5 text-xs text-tertiary">{label}</div>
    </div>
  )
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`
}
